"use client";
import "../../contents/scss/instantOffer.scss";
import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Tooltip from "@mui/material/Tooltip";
import { SanityImageURL } from "@/sanity/Sanity";
import { SellMyCarEasilyPath } from "../common/defaultImage/DefaultPaths";
import sellTrade from "../../../public/media/sell-my-car-bg.jpg";
import Image from "next/image";

import SelllMyCarPopup from "./SelllMyCarPopup";

import Swal from "sweetalert2";
import { useRouter, useSearchParams } from "next/navigation";
import { Alert } from "@mui/material";

const validationSchema = Yup.object({
    vin: Yup.string().matches(/^[A-HJ-NPR-Z0-9]{17}$/i, "Invalid VIN"),
    licensePlate: Yup.string().min(3, "Plate should be at least 3 characters"),
    state: Yup.string()
});

const validate = (values) => {
    const errors = {};

    // Validation logic for section1Field
    if (!values.vin && !values.licensePlate && !values.state) {
        errors.vin = "Vin is required";
    }

    // Validation logic for section2Field1 and section2Field2
    if (!values.vin) {
        if (values.licensePlate && !values.state) {
            errors.state = "State is required";
        }
        if (!values.licensePlate && values.state) {
            errors.licensePlate = "Plate No. is required";
        }
    }

    return errors;
};

export const SellMyCarEasily = ({ sellMyCarEasily, states }) => {
    const [firstOptionSelected, setFirstOptionSelected] = useState(true);
    const [showPopup, setShowPopup] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [popupData, setPopupData] = useState(null);
    const router = useRouter();
    const searchParams = useSearchParams();

    const backgroundImage = sellMyCarEasily?.image
        ? SanityImageURL(sellMyCarEasily.image, SellMyCarEasilyPath)
        : sellTrade.src;

    const handleNext = () => {
        if (currentStep < 8) {
            setCurrentStep(currentStep + 1);
        }
        if (currentStep === 8) {
            showPopup(false);
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
        }
    };

    const handleSubmit = async (values) => {
        let apiUrl = "";
        try {
            // let response;
            if (values.vin) {
                // apiUrl = `/api/decode/vin/${values.vin}`;
                const sp = new URLSearchParams(searchParams);
                sp.set("vin", values.vin);
                router.push(`/sell-my-car/vehicle?${sp.toString()}`);
            } else if (values.licensePlate && values.state) {
                // apiUrl = `/api/decode/license-plate?state=${values.state}&plate=${values.licensePlate}`;
                const sp = new URLSearchParams(searchParams);
                sp.set("licensePlate", values.licensePlate);
                sp.set("plateState", values.state);
                router.push(`/sell-my-car/vehicle?${sp.toString()}`);
            } else {
                console.log("🚀 ~ not called");
                return;
            }
        } catch (error) {
            console.log(error);
            console.error("API Error:", error);
            Swal.fire({
                text: "Could not find the car.",
                icon: "error",
                buttonsStyling: false,
                confirmButtonText: "Ok!",
                customClass: {
                    confirmButton: "btn btn-primary"
                }
            });
        }
    };

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    const [inputs, setInputs] = useState({
        sellTradeIn: "",
        mileage: "",
        yesNo: "no",
        condition: "",
        step4Options: {
            none: false,
            missingParts: false,
            bodyDamage: false,
            paintDamage: false,
            damageToGlass: false,
            damageToWheels: false,
            damageToUpholstery: false,
            other: false
        },
        step4Comments: {
            missingParts: "",
            bodyDamage: "",
            paintDamage: "",
            damageToGlass: "",
            damageToWheels: "",
            damageToUpholstery: "",
            other: ""
        },
        step5Options: {
            none: false,
            engine: false,
            suspension: false,
            fluidLeaks: false,
            dashWarningLights: false,
            tiresBrakesBattery: false,
            acHeater: false,
            powerFeatures: false,
            others: false
        },
        step5Comments: {
            engine: "",
            suspension: "",
            fluidLeaks: "",
            dashWarningLights: "",
            tiresBrakesBattery: "",
            acHeater: "",
            powerFeatures: "",
            others: ""
        },
        fullName: "",
        email: "",
        phoneNumber: ""
    });

    const handleInputChange = (field, value) => {
        setInputs((prevState) => ({
            ...prevState,
            [field]: value
        }));
    };

    const handleOptionChange = (step, option) => {
        const optionsKey = step === 4 ? "step4Options" : "step5Options";
        const commentsKey = step === 4 ? "step4Comments" : "step5Comments";

        if (option === "none") {
            setInputs((prevState) => ({
                ...prevState,
                [optionsKey]: {
                    none: !prevState[optionsKey].none,
                    missingParts: false,
                    bodyDamage: false,
                    paintDamage: false,
                    damageToGlass: false,
                    damageToWheels: false,
                    damageToUpholstery: false,
                    other: false,
                    engine: false,
                    suspension: false,
                    fluidLeaks: false,
                    dashWarningLights: false,
                    tiresBrakesBattery: false,
                    acHeater: false,
                    powerFeatures: false,
                    others: false
                },
                [commentsKey]: {
                    missingParts: "",
                    bodyDamage: "",
                    paintDamage: "",
                    damageToGlass: "",
                    damageToWheels: "",
                    damageToUpholstery: "",
                    other: "",
                    engine: "",
                    suspension: "",
                    fluidLeaks: "",
                    dashWarningLights: "",
                    tiresBrakesBattery: "",
                    acHeater: "",
                    powerFeatures: "",
                    others: ""
                }
            }));
        } else {
            setInputs((prevState) => ({
                ...prevState,
                [optionsKey]: {
                    ...prevState[optionsKey],
                    none: false,
                    [option]: !prevState[optionsKey][option]
                }
            }));
        }
    };

    const handleCommentChange = (step, option, value) => {
        const commentsKey = step === 4 ? "step4Comments" : "step5Comments";
        setInputs((prevState) => ({
            ...prevState,
            [commentsKey]: {
                ...prevState[commentsKey],
                [option]: value
            }
        }));
    };

    const isStepValid = () => {
        switch (currentStep) {
            case 0:
                return inputs.mileage && inputs.mileage >= 1;
            case 1:
                return inputs.yesNo !== "";
            case 2:
                return inputs.condition !== "";
            case 3:
                return true; // Summary step doesn't require validation
            case 4:
                return Object.keys(inputs.step4Options).some((option) => inputs.step4Options[option]);
            case 5:
                return Object.keys(inputs.step5Options).some((option) => inputs.step5Options[option]);
            case 6:
                return inputs.sellTradeIn !== "";
            case 6:
                return inputs.fullName && validateEmail(inputs.email) && validatePhoneNumber(inputs.phoneNumber);
            default:
                return false;
        }
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhoneNumber = (phoneNumber) => {
        const phoneNumberRegex = /^\d{10}$/;
        return phoneNumberRegex.test(phoneNumber);
    };

    return (
        <div
            className="instant-offer-wrapper"
            style={{
                backgroundImage: `url(${backgroundImage})`
            }}>
            {/* <h1 className="py-4">{sellMyCarEasily?.heading}</h1> */}
            <div
                style={{ background: searchParams.get("vendor")?.includes("CarPro") || searchParams.get("vendor")?.includes("ebay") ? "rgba(255,255,255,0.9)" : null }}
                className="container mt-4 form-wrapper">
                {searchParams.get("vendor")?.includes("CarPro") ? (
                    <Image
                        src="/media/carpro_appraisal_pro_logo.png"
                        width={357}
                        height={126}
                    />
                ) : searchParams.get("vendor")?.includes("ebay") ? (
                    <Image
                        src="/media/ebay.png"
                        width={265}
                        height={256}
                    />
                ) : null}
                <h2 className="py-4">
                    {searchParams.get("vendor")?.includes("CarPro")
                        ? "We made Selling Your Car easy!"
                        : sellMyCarEasily?.formHeading}
                </h2>
                {searchParams.get("error") ? (
                    searchParams.get("error") == "duplicate" ? (
                        <div className="mb-4">
                            <Alert color="warning">Vehicle already submitted, please check email</Alert>
                        </div>
                    ) : (
                        <div className="mb-4">
                            <Alert color="error">
                                Please enter a valid{" "}
                                {searchParams.get("error") == "lp" ? "License Plate or try VIN" : "VIN"}
                            </Alert>
                        </div>
                    )
                ) : null}
                <Formik
                    initialValues={{ vin: "", licensePlate: "", state: "" }}
                    validationSchema={validationSchema}
                    validate={validate}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}>
                    {({ setFieldValue }) => (
                        <Form>
                            {/* License Plate and State Fields */}
                            <div className="row mb-3 mobile">
                                <div className="col-8">
                                    <Field
                                        type="text"
                                        id="licensePlate"
                                        name="licensePlate"
                                        className="form-control"
                                        placeholder={sellMyCarEasily?.licensePlaceholder}
                                    />
                                    <ErrorMessage
                                        name="licensePlate"
                                        component="div"
                                        className="text-danger"
                                    />
                                </div>
                                <div className="col-4">
                                    <Field
                                        as="select"
                                        id="state"
                                        name="state"
                                        className="form-select"
                                        onChange={(event) => {
                                            const selectedValue = event.target.value;
                                            setFieldValue("state", selectedValue);
                                            if (firstOptionSelected && selectedValue) {
                                                setFirstOptionSelected(false);
                                            }
                                        }}>
                                        {firstOptionSelected && (
                                            <option
                                                value=""
                                                label={sellMyCarEasily?.statePlaceholder}
                                            />
                                        )}
                                        {states?.map((state, index) => (
                                            <option
                                                key={index}
                                                value={state.value}
                                                label={state.label}
                                            />
                                        ))}
                                        {/* Add more states as needed */}
                                    </Field>
                                    <ErrorMessage
                                        name="state"
                                        component="div"
                                        className="text-danger"
                                    />
                                </div>
                            </div>

                            {/* Separator */}
                            <div className="row my-3">
                                <div className="divider">OR</div>
                            </div>

                            {/* VIN Field */}
                            <div className="row mb-3 align-items-center">
                                <div className="col position-relative">
                                    <Field
                                        type="text"
                                        id="vin"
                                        name="vin"
                                        className="form-control"
                                        placeholder={sellMyCarEasily?.vinPlaceholder}
                                    />
                                    <Tooltip
                                        title="Vehicle Identification Number"
                                        placement="top">
                                        <span
                                            className="question-mark"
                                            data-bs-toggle="tooltip"
                                            data-bs-placement="top">
                                            ?
                                        </span>
                                    </Tooltip>
                                    <ErrorMessage
                                        name="vin"
                                        component="div"
                                        className="text-danger"
                                    />
                                </div>
                            </div>

                            <button
                                className="btn py-4 w-100 custom_btn mt-3 hover_btn"
                                type="submit">
                                {sellMyCarEasily?.btnText}
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="25"
                                    height="25"
                                    fill="currentColor"
                                    className="bi bi-arrow-right-short"
                                    viewBox="0 0 16 16">
                                    <path
                                        fillRule="evenodd"
                                        d="M4 8a.5.5 0 0 1 .5-.5h5.793L8.146 5.354a.5.5 0 1 1 .708-.708l3 3a.5.5 0 0 1 0 .708l-3 3a.5.5 0 0 1-.708-.708L10.293 8.5H4.5A.5.5 0 0 1 4 8z"
                                    />
                                </svg>
                            </button>
                        </Form>
                    )}
                </Formik>
            </div>
            {showPopup && (
                <SelllMyCarPopup
                    data={popupData}
                    onClose={handleClosePopup}
                    currentStep={currentStep}
                    handleNext={handleNext}
                    handleBack={handleBack}
                    inputs={inputs}
                    handleInputChange={handleInputChange}
                    handleOptionChange={handleOptionChange}
                    handleCommentChange={handleCommentChange}
                    isStepValid={isStepValid}
                />
            )}
        </div>
    );
};
